import { FC, useEffect, useMemo, useState } from 'react'
import { Image, Grid, Row, Col } from 'react-bootstrap'
import Modal from './Modal'
import TrashIcon from '../../../components/UI/Icons/Trash'
import { MEDIA_DATA_TYPE, POST_SOCIAL_STATUS } from '../../../models/post'
import CanvaButton, { TCanvaPayload } from '../../PostCreation/CanvaButton/CanvaButton'
import LoadableImage, { LOADER_STYLE } from '../../UI/LoadableImage'
import CustomPopup from '../../UI/Popup'
import { useCallback } from 'react'

export enum IMAGE_GRID_TYPE {
  FB_GRID = 'fb-grid',
  INSTA_GRID = 'insta-grid'
}
enum HEIGHT_RELATIVE_TO_CONTAINER {
  MAX_HEIGHT = 150,
  FULL = 100,
  HALF = 50,
  THIRD = 33.3,
  TWO_THIRD = 66.6
}

enum WIDTH_RELATIVE_TO_CONTAINER {
  FULL = 12,
  HALF = 6,
  THIRD = 4,
  TWO_THIRD = 8
}

type TDateData = {
  status: POST_SOCIAL_STATUS
  isAutoposting: boolean
  date: string
  isMokup: boolean
}

export type TGridImage = MEDIA_DATA_TYPE & {
  dateData?: TDateData
}
interface ImagesProps {
  images: TGridImage[]
  hideOverlay: boolean
  renderOverlay?: () => string
  overlayBackgroundColor?: string
  onClickEach?: null | (() => never)
  firstImageHeight?: number | null
  countFrom?: number
  onRemove?: (index: number) => void
  onUpdateCanvaImage?: (payload: TCanvaPayload) => void
  isEditable: boolean
  gridType?: IMAGE_GRID_TYPE
}

const Images: FC<ImagesProps> = ({
  images = [],
  hideOverlay = false,
  renderOverlay: renderOverlayProps = () => 'Preview Image',
  overlayBackgroundColor = '#222222',
  countFrom: countFromProps = 5,
  onClickEach = null,
  firstImageHeight = null,
  onRemove,
  gridType = IMAGE_GRID_TYPE.FB_GRID,
  onUpdateCanvaImage,
  isEditable
}) => {
  const [modal, setModal] = useState<boolean>(false)
  const [countFrom, setCountFrom] = useState<number>(
    countFromProps > 0 && countFromProps < 5 ? countFromProps : 5
  )
  const [conditionalRender, setConditionalRender] = useState<boolean>(false)
  const [url, setUrl] = useState<string>('')
  const [index, setIndex] = useState<number>(0)

  useEffect(() => {
    if (countFromProps <= 0 || countFromProps > 5) {
      // console.warn('countFrom is limited to 5!')
    }
  }, [])

  const imagesToShow = useMemo(() => {
    if (countFrom && images.length > countFrom) {
      const imagesLocalCopy = [...images]
      imagesLocalCopy.length = countFrom
      return imagesLocalCopy
    } else {
      return images
    }
  }, [images, countFrom])

  const removeAndUpdate = (index: number) => {
    const prevIndex = index - 1
    if (prevIndex < 0) {
      setModal(false)
      onRemove && onRemove(index)
    } else {
      setIndex(prevIndex)
      onRemove && onRemove(index)
    }
  }

  const openModal = (index: number) => {
    if (onClickEach) {
      return onClickEach()
    }
    setModal(true)
    setUrl(images[index].public_url)
    setIndex(index)
  }

  const onClose = (): void => {
    setModal(false)
  }

  const renderOverlay = (id?: number) => {
    if (hideOverlay) {
      return false
    }

    return [
      <div
        key={`cover-${id}`}
        className='cover slide'
        style={{ backgroundColor: overlayBackgroundColor }}
      ></div>,
      <div
        key={`cover-text-${id}`}
        className='cover-text slide animate-text'
        style={{ fontSize: '100%' }}
      >
        {renderOverlayProps()}
      </div>
    ]
  }

  const renderCountOverlay = (more: boolean) => {
    const extra = images.length - (countFrom && countFrom > 5 ? 5 : countFrom)

    return [
      more && <div key='count' className='cover'></div>,
      more && (
        <div key='count-sub' className='cover-text' style={{ fontSize: '200%' }}>
          <p>+{extra}</p>
        </div>
      )
    ]
  }

  const { isVerticalOrientation, imageWidth, imageHeight } = useMemo(() => {
    const { width: imageWidth, height: imageHeight } = images[0].options
    return {
      isVerticalOrientation: imageWidth / imageHeight < 1,
      imageWidth,
      imageHeight
    }
  }, [images[0]])

  const renderOne = () => {
    const overlay =
      images.length > countFrom && countFrom == 1 ? renderCountOverlay(true) : renderOverlay()

    let calculatedHeight = 0,
      calculatedWidth = 0

    if (isVerticalOrientation) {
      switch (images.length) {
        case 3: {
          // +1 covers paddings beetwen grid to the right
          calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.FULL + 1
          calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.HALF
          break
        }
        case 4: {
          // +2 covers paddings beetwen grid to the right
          calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.FULL + 2
          calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.TWO_THIRD
          break
        }
        default: {
          calculatedHeight = (HEIGHT_RELATIVE_TO_CONTAINER.FULL / imageWidth) * imageHeight
          calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.FULL

          if (calculatedHeight > HEIGHT_RELATIVE_TO_CONTAINER.MAX_HEIGHT) {
            calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.MAX_HEIGHT
          }
        }
      }
    } else {
      switch (images.length) {
        case 3: {
          calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.HALF
          calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.FULL
          break
        }
        case 4: {
          calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.TWO_THIRD
          calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.FULL
          break
        }
        default: {
          calculatedHeight = (HEIGHT_RELATIVE_TO_CONTAINER.FULL / imageWidth) * imageHeight
          calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.FULL

          if (calculatedHeight > HEIGHT_RELATIVE_TO_CONTAINER.MAX_HEIGHT) {
            calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.MAX_HEIGHT
          }
        }
      }
    }

    return (
      <Grid>
        <Row>
          <Col
            xs={calculatedWidth}
            md={calculatedWidth}
            className={`border height-one background`}
            style={{ paddingTop: `${firstImageHeight || calculatedHeight}%` }}
          >
            <LoadableImage
              key={`post-image-1-1-${images[0].public_url}`}
              media={images[0].public_url}
            />
            {onRemove && isEditable && (
              <div className='image-actions'>
                {images[0].canva_design_id && onUpdateCanvaImage && (
                  <div className='image-actions_canva-edit'>
                    <CanvaButton
                      designId={images[0].canva_design_id}
                      updateImageData={onUpdateCanvaImage}
                    />
                  </div>
                )}
                <div className='image-actions_remove' onClick={() => onRemove(0)}>
                  <TrashIcon />
                </div>
              </div>
            )}
            <div className='overlay' onClick={() => openModal(0)}>
              {overlay}
            </div>
          </Col>
        </Row>
      </Grid>
    )
  }

  const renderTwo = () => {
    const overlay =
      images.length > countFrom && [2, 3].includes(+countFrom)
        ? renderCountOverlay(true)
        : renderOverlay()
    const conditionalRender =
      [3, 4].includes(images.length) || (images.length > +countFrom && [3, 4].includes(+countFrom))

    let calculatedHeight
    let calculatedWidth

    if (isVerticalOrientation) {
      switch (images.length) {
        case 2: {
          calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.FULL
          calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.HALF
          break
        }
        case 3: {
          calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.HALF
          calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.HALF
          break
        }
        case 5: {
          calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.TWO_THIRD
          calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.HALF
          break
        }
        default: {
          calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.TWO_THIRD
          calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.HALF
          break
        }
      }
    } else {
      switch (images.length) {
        case 2: {
          calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.HALF
          calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.FULL
          break
        }
        case 3: {
          calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.HALF
          calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.HALF
          break
        }
        case 5: {
          calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.TWO_THIRD
          calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.HALF
          break
        }
        default: {
          calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.TWO_THIRD
          calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.HALF
          break
        }
      }
    }

    return (
      <Grid>
        <Row>
          <Col
            xs={calculatedWidth}
            md={calculatedWidth}
            className='border height-two background'
            style={{ paddingTop: `${calculatedHeight}%` }}
          >
            <LoadableImage
              key={`post-image-2-1-${
                conditionalRender ? images[1].public_url : images[0].public_url
              }`}
              media={conditionalRender ? images[1].public_url : images[0].public_url}
            />
            {onRemove && isEditable && (
              <div className='image-actions'>
                {!onUpdateCanvaImage
                  ? null
                  : conditionalRender
                  ? images[1].canva_design_id && (
                      <div className='image-actions_canva-edit'>
                        <CanvaButton
                          designId={images[1].canva_design_id}
                          updateImageData={onUpdateCanvaImage}
                        />
                      </div>
                    )
                  : images[0].canva_design_id && (
                      <div className='image-actions_canva-edit'>
                        <CanvaButton
                          designId={images[0].canva_design_id}
                          updateImageData={onUpdateCanvaImage}
                        />
                      </div>
                    )}
                <div
                  className='image-actions_remove'
                  onClick={() => onRemove(conditionalRender ? 1 : 0)}
                >
                  <TrashIcon />
                </div>
              </div>
            )}
            <div className='overlay' onClick={() => openModal(conditionalRender ? 1 : 0)}>
              {renderOverlay()}
            </div>
          </Col>
          <Col
            xs={calculatedWidth}
            md={calculatedWidth}
            className='border height-two background'
            style={{ paddingTop: `${calculatedHeight}%` }}
          >
            <LoadableImage
              key={`post-image-2-2-${
                conditionalRender ? images[2].public_url : images[1].public_url
              }`}
              media={conditionalRender ? images[2].public_url : images[1].public_url}
            />

            {onRemove && isEditable && (
              <div className='image-actions'>
                {!onUpdateCanvaImage
                  ? null
                  : conditionalRender
                  ? images[2].canva_design_id && (
                      <div className='image-actions_canva-edit'>
                        <CanvaButton
                          designId={images[2].canva_design_id}
                          updateImageData={onUpdateCanvaImage}
                        />
                      </div>
                    )
                  : images[1].canva_design_id && (
                      <div className='image-actions_canva-edit'>
                        <CanvaButton
                          designId={images[1].canva_design_id}
                          updateImageData={onUpdateCanvaImage}
                        />
                      </div>
                    )}
                <div
                  className='image-actions_remove'
                  onClick={() => onRemove(conditionalRender ? 2 : 1)}
                >
                  <TrashIcon />
                </div>
              </div>
            )}
            <div className='overlay' onClick={() => openModal(conditionalRender ? 2 : 1)}>
              {overlay}
            </div>
          </Col>
        </Row>
      </Grid>
    )
  }

  const renderThree = () => {
    const conditionalRender = images.length == 4 || (images.length > +countFrom && +countFrom == 4)
    const overlayType =
      !countFrom || countFrom > 5 || (images.length > countFrom && [4, 5].includes(+countFrom))
    const overlay = overlayType
      ? renderCountOverlay(true)
      : renderOverlay(conditionalRender ? 3 : 4)

    let calculatedHeight = 0,
      calculatedWidth = 0

    if (isVerticalOrientation) {
      switch (images.length) {
        default: {
          calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.THIRD
          calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.THIRD
          break
        }
      }
    } else {
      switch (images.length) {
        default: {
          calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.THIRD
          calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.THIRD
          break
        }
      }
    }

    return (
      <Grid>
        <Row>
          <Col
            xs={calculatedWidth}
            md={calculatedWidth}
            className='border height-three background'
            style={{ paddingTop: `${calculatedHeight}%` }}
          >
            <LoadableImage
              key={`post-image-3-1-${
                conditionalRender ? images[1].public_url : images[2].public_url
              }`}
              media={conditionalRender ? images[1].public_url : images[2].public_url}
            />
            {onRemove && isEditable && (
              <div className='image-actions'>
                {!onUpdateCanvaImage
                  ? null
                  : conditionalRender
                  ? images[1].canva_design_id && (
                      <div className='image-actions_canva-edit'>
                        <CanvaButton
                          designId={images[1].canva_design_id}
                          updateImageData={onUpdateCanvaImage}
                        />
                      </div>
                    )
                  : images[2].canva_design_id && (
                      <div className='image-actions_canva-edit'>
                        <CanvaButton
                          designId={images[2].canva_design_id}
                          updateImageData={onUpdateCanvaImage}
                        />
                      </div>
                    )}
                <div
                  className='image-actions_remove'
                  onClick={() => onRemove(conditionalRender ? 1 : 2)}
                >
                  <TrashIcon />
                </div>
              </div>
            )}
            <div className='overlay' onClick={() => openModal(conditionalRender ? 1 : 2)}>
              {renderOverlay(conditionalRender ? 1 : 2)}
            </div>
          </Col>
          <Col
            xs={calculatedWidth}
            md={calculatedWidth}
            className='border height-three background'
            style={{ paddingTop: `${calculatedHeight}%` }}
          >
            <LoadableImage
              key={`post-image-3-2-${
                conditionalRender ? images[2].public_url : images[3].public_url
              }`}
              media={conditionalRender ? images[2].public_url : images[3].public_url}
            />
            {onRemove && isEditable && (
              <div className='image-actions'>
                {!onUpdateCanvaImage
                  ? null
                  : conditionalRender
                  ? images[2].canva_design_id && (
                      <div className='image-actions_canva-edit'>
                        <CanvaButton
                          designId={images[2].canva_design_id}
                          updateImageData={onUpdateCanvaImage}
                        />
                      </div>
                    )
                  : images[3].canva_design_id && (
                      <div className='image-actions_canva-edit'>
                        <CanvaButton
                          designId={images[3].canva_design_id}
                          updateImageData={onUpdateCanvaImage}
                        />
                      </div>
                    )}
                <div
                  className='image-actions_remove'
                  onClick={() => onRemove(conditionalRender ? 2 : 3)}
                >
                  <TrashIcon />
                </div>
              </div>
            )}
            <div className='overlay' onClick={() => openModal(conditionalRender ? 2 : 3)}>
              {renderOverlay(conditionalRender ? 2 : 3)}
            </div>
          </Col>
          {overlayType ? (
            <Col
              xs={calculatedWidth}
              md={calculatedWidth}
              className='border height-three background'
              style={{ paddingTop: `${calculatedHeight}%` }}
            >
              <LoadableImage
                key={`post-image-3-3-${
                  conditionalRender ? images[3].public_url : images[4].public_url
                }`}
                media={conditionalRender ? images[3].public_url : images[4].public_url}
              />
              <div className='overlay' onClick={() => openModal(conditionalRender ? 3 : 4)}>
                {overlay}
              </div>
            </Col>
          ) : (
            <Col
              xs={calculatedWidth}
              md={calculatedWidth}
              className='border height-three background'
              style={{ paddingTop: `${calculatedHeight}%` }}
            >
              <LoadableImage
                key={`post-image-3-3-${
                  conditionalRender ? images[3].public_url : images[4].public_url
                }`}
                media={conditionalRender ? images[3].public_url : images[4].public_url}
              />
              {onRemove && isEditable && (
                <div className='image-actions'>
                  {!onUpdateCanvaImage
                    ? null
                    : conditionalRender
                    ? images[3].canva_design_id && (
                        <div className='image-actions_canva-edit'>
                          <CanvaButton
                            designId={images[3].canva_design_id}
                            updateImageData={onUpdateCanvaImage}
                          />
                        </div>
                      )
                    : images[4].canva_design_id && (
                        <div className='image-actions_canva-edit'>
                          <CanvaButton
                            designId={images[4].canva_design_id}
                            updateImageData={onUpdateCanvaImage}
                          />
                        </div>
                      )}
                  <div
                    className='image-actions_remove'
                    onClick={() => onRemove(conditionalRender ? 3 : 4)}
                  >
                    <TrashIcon />
                  </div>
                </div>
              )}
              <div className='overlay' onClick={() => openModal(conditionalRender ? 3 : 4)}>
                {renderOverlay(conditionalRender ? 3 : 4)}
              </div>
            </Col>
          )}
        </Row>
      </Grid>
    )
  }

  const generateDateTextUsingPostStatus = useCallback((dateData: TDateData) => {
    let statusText, dateText

    const { date, isAutoposting, status, isMokup } = dateData

    const transformedData = `${
      isAutoposting ? 'Scheduled' : 'Pending manual publish'
    } on the ${date}`

    switch (status) {
      case POST_SOCIAL_STATUS.STATUS_DRAFT:
        statusText = `Draft post.`
        dateText = transformedData
        break
      case POST_SOCIAL_STATUS.STATUS_LIVE:
        statusText = `Post was published`
        dateText = `on the ${date}`
        break
      case POST_SOCIAL_STATUS.STATUS_ON_PUBLISHING:
        statusText = `Post starts publishing process`
        dateText = `on the ${date}`
        break
      case POST_SOCIAL_STATUS.STATUS_PENDING_EXTERNAL_APPROVAL:
        statusText = `Pending external approval.`
        dateText = transformedData
        break
      case POST_SOCIAL_STATUS.STATUS_PENDING_INTERNAL_APPROVAL:
        statusText = `Pending internal approval.`
        dateText = transformedData
        break
      case POST_SOCIAL_STATUS.STATUS_PUBLISHING_FAILED:
        statusText = `Publishing failed`
        dateText = `on the ${date}`
        break
      case POST_SOCIAL_STATUS.STATUS_REQUIRES_REVISION:
        statusText = `Requires revision post.`
        dateText = transformedData
        break
      default:
        statusText = ``
        dateText = transformedData
    }

    if (isMokup) {
      statusText = 'Post from Instagram.'
      dateText = `Published on the ${date}`
    }

    return {
      statusText,
      dateText
    }
  }, [])

  const renderNine = () => {
    const calculatedHeight = HEIGHT_RELATIVE_TO_CONTAINER.THIRD,
      calculatedWidth = WIDTH_RELATIVE_TO_CONTAINER.THIRD

    const imagesToRender = [...images].splice(0, 9)

    return (
      <Grid className='instagram-posts-grid grid-cover'>
        <Row>
          {imagesToRender.map((objectData, index) => {
            const popupData = generateDateTextUsingPostStatus(objectData.dateData!)
            return (
              <CustomPopup
                className='instagram-posts-grid__popup'
                key={`instagram-grid-image-${index}-${objectData.public_url}`}
                on='hover'
                position='top center'
                offsetY={-10}
                offsetX={10}
                trigger={() => (
                  <div
                    key={`insta-grid-image-${index}`}
                    style={{ paddingTop: `${calculatedHeight}%` }}
                    className={`border height-three background col-md-${calculatedWidth} col-xs-${calculatedWidth}`}
                  >
                    {objectData.public_url ? (
                      <>
                        <LoadableImage media={objectData.public_url} />
                        <div className='overlay' onClick={() => openModal(index)} />
                      </>
                    ) : (
                      <div className='instagram-posts-grid__empty-placeholder'>
                        Draft post without media
                      </div>
                    )}
                  </div>
                )}
              >
                <div className='instagram-posts-grid__text'>{popupData.statusText}</div>
                <div className='instagram-posts-grid__text'>{popupData.dateText}</div>
              </CustomPopup>
            )
          })}
        </Row>
      </Grid>
    )
  }

  const renderGrid = () => {
    switch (gridType) {
      case IMAGE_GRID_TYPE.FB_GRID:
        return (
          <>
            {[1, 3, 4].includes(imagesToShow.length) && renderOne()}
            {imagesToShow.length >= 2 && imagesToShow.length != 4 && renderTwo()}
            {imagesToShow.length >= 4 && renderThree()}
          </>
        )
      case IMAGE_GRID_TYPE.INSTA_GRID:
        return <>{renderNine()}</>

      default:
        return null
    }
  }

  return (
    <div className='grid-container'>
      {renderGrid()}
      {modal && images[index] && (
        <Modal
          onUpdateCanvaImage={onUpdateCanvaImage}
          isEditable={isEditable}
          onRemove={removeAndUpdate}
          onClose={onClose}
          index={index}
          images={images}
        />
      )}
    </div>
  )
}

export default Images
